'use client';

import { useState, useEffect, useCallback, Fragment, FC } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Collapse,
  CircularProgress,
  Stack,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { useBackend } from '@/hooks/useBackend';
import { SampleRequestDto, PrivilegeEnum, RequestStatusDto } from '@/api/ApiClient';
import { formatDate } from '@/lib/utils/dateUtils';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { log } from '@/lib/utils/logger';
import { useSnackbar } from '@/hooks/useSnackbar';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useDebounce } from '@/hooks/useDebounce';
import { getProblemDetail } from '@/lib/utils/apiErrors';

export const selectOptions: OptionKey[] = ['sampleRequestStatus'];

/**
 * Gets the display label for a request status code
 */
const getRequestStatusLabel = (statusCode: string | undefined, options?: { key: string; value: string }[]): string => {
  if (!statusCode) return 'N/A';
  if (!options) return statusCode;
  const option = options.find((opt) => opt.key === statusCode);
  return option?.value ?? statusCode;
};

interface ProjectSampleRequestsTabProps {
  projectId: number;
  projectLabel: string;
  isReadOnly: boolean;
  /** When set, new rows default their due date to this value (typically the project end date). */
  projectEndDate?: Date | string;
}

interface SampleRequestRow extends SampleRequestDto {
  selected?: boolean;
  isNew?: boolean;
}

export const ProjectSampleRequestsTab: FC<ProjectSampleRequestsTabProps> = ({
  projectId,
  projectLabel,
  isReadOnly,
  projectEndDate,
}) => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { resolveUser } = useUserResolution();
  const { isAuthorized } = usePrivileges();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { setSampleRequests, getSampleRequests, setDataModified, isDataModified } = useTree();

  // Use a special dataId format for sample requests to track modifications
  const sampleRequestsDataId = `sampleRequests-project-${projectId}`;

  // Local state for requests with UI-only fields (isNew, selected)
  // Tree store only contains clean DTOs
  const cachedRequests = getSampleRequests(projectId);
  const [requests, setRequests] = useState<SampleRequestRow[]>(
    cachedRequests ? cachedRequests.map((req) => ({ ...req, selected: false, isNew: false })) : []
  );
  const [loading, setLoading] = useState(!cachedRequests);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const hasUnsavedChanges = isDataModified(sampleRequestsDataId);
  const [isSaving, setIsSaving] = useState(false);

  const canAddRequest = isAuthorized(PrivilegeEnum.SampleRequest, 'create');
  const canDeleteRequest = isAuthorized(PrivilegeEnum.SampleRequest, 'delete');

  const loadRequests = useCallback(async () => {
    if (!backend || !projectLabel || !projectId) return;

    // Check if already loaded in tree store
    const cached = getSampleRequests(projectId);
    if (cached) {
      // Use cached data
      setRequests(
        cached.map((req) => ({
          ...req,
          // Convert string dates to Date objects if needed
          dueDate: req.dueDate ? (typeof req.dueDate === 'string' ? new Date(req.dueDate) : req.dueDate) : undefined,
          selected: false,
          isNew: false,
        }))
      );
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const loadedRequests = await backend.project.getSampleRequests(projectLabel);
      setRequests(
        loadedRequests.map((req) => ({
          ...req,
          // Convert string dates to Date objects if needed
          dueDate: req.dueDate ? (typeof req.dueDate === 'string' ? new Date(req.dueDate) : req.dueDate) : undefined,
          selected: false,
          isNew: false,
        }))
      );
      // Store clean DTOs in tree store (without UI-only fields)
      setSampleRequests(projectId, loadedRequests);
      setDataModified(sampleRequestsDataId, false);
    } catch (error) {
      log.error('[ProjectSampleRequestsTab] Error loading sample requests:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to load sample requests.', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    backend,
    projectLabel,
    projectId,
    getSampleRequests,
    setSampleRequests,
    setDataModified,
    sampleRequestsDataId,
    showSnackbar,
  ]);

  useEffect(() => {
    if (cachedRequests || !backend || !projectLabel || !projectId) return;
    let cancelled = false;

    const loadInitialRequests = async () => {
      try {
        const loadedRequests = await backend.project.getSampleRequests(projectLabel);
        if (cancelled) return;
        setRequests(
          loadedRequests.map((req) => ({
            ...req,
            dueDate: req.dueDate ? (typeof req.dueDate === 'string' ? new Date(req.dueDate) : req.dueDate) : undefined,
            selected: false,
            isNew: false,
          }))
        );
        setSampleRequests(projectId, loadedRequests);
        setDataModified(sampleRequestsDataId, false);
      } catch (error) {
        if (cancelled) return;
        log.error('[ProjectSampleRequestsTab] Error loading sample requests:', error);
        showSnackbar(getProblemDetail(error) ?? 'Failed to load sample requests.', 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadInitialRequests();
    return () => {
      cancelled = true;
    };
  }, [
    backend,
    projectLabel,
    projectId,
    cachedRequests,
    setSampleRequests,
    setDataModified,
    sampleRequestsDataId,
    showSnackbar,
  ]);

  const handleSelectRequest = (index: number) => {
    if (isReadOnly && !requests[index].description) return;

    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      // Close all other rows
      newExpanded.clear();
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const handleCreateRequest = () => {
    // Get the first status option as default (status is required)
    const defaultStatus =
      options.sampleRequestStatus && options.sampleRequestStatus.length > 0
        ? options.sampleRequestStatus[0].key
        : undefined;

    const defaultDueDate = projectEndDate
      ? typeof projectEndDate === 'string'
        ? new Date(projectEndDate)
        : projectEndDate
      : undefined;

    const newRequest: SampleRequestRow = {
      projectId,
      projectLabel,
      title: '',
      count: undefined,
      requestStatus: defaultStatus as RequestStatusDto | undefined,
      dueDate: defaultDueDate,
      description: '',
      selected: false,
      isNew: true,
    };

    const newRequests = [...requests, newRequest];
    setRequests(newRequests);
    setExpandedRows(new Set([newRequests.length - 1]));
    // Store clean DTOs in tree store (strip UI-only fields) and mark as modified
    const dtos = newRequests.map(({ selected: _selected, isNew: _isNew, ...dto }) => dto);
    setSampleRequests(projectId, dtos);
    setDataModified(sampleRequestsDataId, true);
  };

  const handleDeleteRequest = (index: number) => {
    const newRequests = requests.filter((_, i) => i !== index);
    setRequests(newRequests);
    setExpandedRows(new Set());
    // Store clean DTOs in tree store (strip UI-only fields) and mark as modified
    const dtos = newRequests.map(({ selected: _selected, isNew: _isNew, ...dto }) => dto);
    setSampleRequests(projectId, dtos);
    setDataModified(sampleRequestsDataId, true);
  };

  const handleUpdateRequest = useCallback(
    (index: number, updates: Partial<SampleRequestDto>) => {
      const newRequests = [...requests];
      newRequests[index] = { ...newRequests[index], ...updates };
      setRequests(newRequests);
      // Store clean DTOs in tree store (strip UI-only fields) and mark as modified
      const dtos = newRequests.map(({ selected: _selected, isNew: _isNew, ...dto }) => dto);
      setSampleRequests(projectId, dtos);
      setDataModified(sampleRequestsDataId, true);
    },
    [requests, projectId, setSampleRequests, setDataModified, sampleRequestsDataId]
  );

  // Debounced handler for description updates (RichTextEditor)
  const debouncedUpdateDescription = useDebounce(
    useCallback(
      (index: number, description: string) => {
        handleUpdateRequest(index, { description });
      },
      [handleUpdateRequest]
    ),
    500
  );

  const handleSave = useCallback(async () => {
    if (!backend || isSaving) return;

    setIsSaving(true);
    try {
      // Filter out empty new requests
      const requestsToSave = requests.filter((req) => {
        if (req.isNew) {
          return req.title?.trim() || req.description?.trim();
        }
        return true;
      });

      if (requestsToSave.length === 0) {
        showSnackbar('No sample requests to save', 'info');
        setIsSaving(false);
        return;
      }

      // Convert to DTOs (strip UI-only fields)
      const dtos: SampleRequestDto[] = requestsToSave.map(({ selected: _selected, isNew: _isNew, ...dto }) => dto);

      await backend.project.updateSampleRequests(projectId, dtos);
      showSnackbar('Sample requests saved successfully', 'success');
      await loadRequests();
      // Clear modified flag (handled by loadRequests)
    } catch (error) {
      log.error('[ProjectSampleRequestsTab] Error saving sample requests:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save sample requests.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [backend, isSaving, requests, projectId, showSnackbar, loadRequests]);

  const handleCancel = useCallback(async () => {
    // Reload from server to undo changes (this will clear the modified flag)
    await loadRequests();
  }, [loadRequests]);

  if (optionsLoading || loading) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading sample requests...
        </Typography>
      </Paper>
    );
  }

  if (!options.sampleRequestStatus) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Unable to load sample request options
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sample Requests
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Title</TableCell>
                <TableCell width={120}># requested</TableCell>
                <TableCell width={130}>Status</TableCell>
                <TableCell width={150}>Due Date</TableCell>
                <TableCell width={150}>Modification Date</TableCell>
                <TableCell width={150}>Modified By</TableCell>
                <TableCell width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request, index) => {
                const isExpanded = expandedRows.has(index);
                const canDelete = canDeleteRequest;

                const rowKey = request.id || `new-${index}`;
                return (
                  <Fragment key={rowKey}>
                    <TableRow>
                      <TableCell>
                        {!isReadOnly || request.description ? (
                          <IconButton
                            size="small"
                            onClick={() => handleSelectRequest(index)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {isReadOnly ? (
                          <Typography variant="body2" sx={{ px: 1 }}>
                            {request.title || ''}
                          </Typography>
                        ) : (
                          <DebouncedTextField
                            value={request.title || ''}
                            onUpdate={(value) => handleUpdateRequest(index, { title: value })}
                            size="small"
                            fullWidth
                            placeholder="Request title"
                            updateMode="debounce"
                            debounceDelay={500}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnly ? (
                          <Typography variant="body2" sx={{ px: 1 }}>
                            {request.count ?? 'Several'}
                          </Typography>
                        ) : (
                          <DebouncedTextField
                            type="number"
                            value={request.count?.toString() ?? ''}
                            onUpdate={(value) => {
                              const numValue = value === '' ? undefined : parseInt(value, 10);
                              handleUpdateRequest(index, { count: numValue });
                            }}
                            size="small"
                            fullWidth
                            placeholder="Several"
                            inputProps={{ min: 1 }}
                            title="Number >= 1. Leave empty if number is not specified (several samples)"
                            updateMode="debounce"
                            debounceDelay={500}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnly ? (
                          <Typography variant="body2" sx={{ px: 1 }}>
                            {getRequestStatusLabel(request.requestStatus, options.sampleRequestStatus)}
                          </Typography>
                        ) : (
                          <FormControl fullWidth size="small">
                            <Select
                              value={request.requestStatus || ''}
                              onChange={(e) =>
                                handleUpdateRequest(index, { requestStatus: e.target.value || undefined })
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Any</em>
                              </MenuItem>
                              {options.sampleRequestStatus
                                ? options.sampleRequestStatus.map((status) => (
                                    <MenuItem key={status.key} value={status.key}>
                                      {status.value}
                                    </MenuItem>
                                  ))
                                : null}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnly ? (
                          <Typography variant="body2" sx={{ px: 1 }}>
                            {request.dueDate ? formatDate(request.dueDate) : 'not set'}
                          </Typography>
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              value={request.dueDate ? new Date(request.dueDate) : null}
                              onChange={(newValue) => {
                                handleUpdateRequest(index, {
                                  dueDate: newValue ? newValue : undefined,
                                });
                              }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: true,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ px: 1 }}>
                          {request.lastChangedDate ? formatDate(request.lastChangedDate) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ px: 1 }}>
                          {request.lastChangedByUser ? resolveUser(request.lastChangedByUser) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {canDelete && !isReadOnly ? (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRequest(index)}
                            aria-label="Delete Request"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            {!isReadOnly ? (
                              <RichTextEditor
                                value={request.description || ''}
                                onChange={(value) => debouncedUpdateDescription(index, value)}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ px: 1, py: 1 }}
                                dangerouslySetInnerHTML={{ __html: request.description || '' }}
                              />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {canAddRequest && !isReadOnly ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleCreateRequest}>
                      New
                    </Button>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer with Save/Cancel buttons */}
      {!isReadOnly && hasUnsavedChanges ? (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, backgroundColor: 'background.paper' }}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="contained" color="error" size="large" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Box>
      ) : null}
    </>
  );
};

export default ProjectSampleRequestsTab;
